import classNames from 'classnames';
import { forwardRef, useEffect } from 'react';
import { Tariff } from '@/entities';
import { Privilege } from '@/entities/privilege';
import Button from '@/shared/components/shared/Button';
import Typografed from '@/shared/components/shared/Typografed';
import { DICT } from '@/shared/config/dict';
import { useLocale } from '@/shared/hooks/use-locale';
import { ImageShape } from '@/shared/lib/types';
import { tp } from '@/shared/lib/typograf';
import Link from '../../shared/Link';
import RoundButton from '../../shared/RoundButton';

export type TariffsInfoSectionProps = {
    title: string;
    subtitle: string | null;
    link?: {
        name: string;
        href: string;
    } | null;
    allTariffFeatures: Privilege[];
    tariffs: Tariff[];
    discounts?: { tariffId: number; price: string }[];
    oferta?: {
        src: string;
        alt: string;
        title: string;
    } | null;
    showDescriptionOfPrice?: boolean;
    VAT?: boolean;
    notes?: string[];
    contacts?: {
        title?: string | null;
        text?: string | null;
        items: {
            href: string;
            label: string;
            icon?: ImageShape;
        }[];
    } | null;
};

type Props = React.HTMLAttributes<HTMLElement> & TariffsInfoSectionProps;

export const SECTION_ID = 'tarifs';

const TariffsInfoSection = forwardRef<HTMLDivElement, Props>(
    (
        {
            title,
            subtitle,
            link,
            allTariffFeatures,
            tariffs,
            discounts,
            oferta,
            showDescriptionOfPrice = true,
            VAT = false,
            notes,
            contacts,
            ...props
        },
        ref,
    ) => {
        const locale = useLocale();
        const tariffsInfo = tariffs.map((tariff) => {
            return {
                id: tariff.id,
                title: tariff.title,
                signature: tariff.signature,
                features: allTariffFeatures
                    .filter((feature) => tariff.features.includes(feature.id))
                    .sort((a, b) => tariff.features.indexOf(a.id) - tariff.features.indexOf(b.id))
                    .map((feature) => ({
                        id: feature.id,
                        name: feature.name,
                    })),
                fullPrice: tariff.futurePrice,
                discountPrice: tariff.price,
                mainTariff: tariff.isMain || false,
                privilegeTitle: tariff.privilegeTitle,
                button: {
                    text: tariff.button?.text || 'Принять участие',
                    href: tariff.button?.href || '/registration/',
                    isExternal: tariff.button?.isExternal || false,
                },
            };
        });

        useEffect(() => {
            const containers = document.querySelectorAll<HTMLElement>('.js-tariff-title__container');
            let minHeight = 0;
            containers.forEach((el) => {
                minHeight = Math.max(minHeight, el.offsetHeight);
            });
            const list = document.querySelector<HTMLElement>('.js-tariffs-info-section__list');
            if (list) {
                list.style.setProperty('--tariff-title-min-height', minHeight + 'px');
            }
        }, [tariffsInfo.length]);

        return (
            <section
                {...props}
                ref={ref}
                className={classNames('tariffs-info-section wrapper js-header-theme-trigger', props.className)}
                data-theme="dark"
                id={props.id ?? SECTION_ID}
            >
                <div className="tariffs-info-section__top">
                    <div className="h3 tariffs-info-section__title">
                        <Typografed>{title}</Typografed>
                    </div>
                    <div className="tariffs-info-section__top-right">
                        {subtitle && (
                            <p className="text-s tariffs-info-section__top-text">
                                <Typografed>{subtitle}</Typografed>
                            </p>
                        )}
                        {link && link.href && (
                            <Button
                                tag={Link}
                                href={link.href}
                                target="_blank"
                                rel="noreferrer nofollow"
                                className="tariffs-info-section__account-btn"
                                variant="outline-dark"
                            >
                                {link.name}
                            </Button>
                        )}
                    </div>
                </div>

                <div className="tariffs-info-section__table-wrapper">
                    <div className="tariffs-info-section__table-wrapper-inner">
                        <ul
                            className={classNames(
                                'tariffs-info-section__list js-tariffs-info-section__list list-unstyled',
                                {
                                    [`tariffs-info-section__list--${tariffsInfo.length}`]: tariffsInfo,
                                },
                            )}
                        >
                            {tariffsInfo.map((tariff) => (
                                <li
                                    className={classNames('tariff-card', {
                                        'tariff-card--main': tariff.mainTariff,
                                    })}
                                    key={tariff.id}
                                >
                                    <div className="tariff-title__container js-tariff-title__container">
                                        <div className="tariff-title__title text-xl">
                                            <Typografed>{tariff.title}</Typografed>
                                        </div>
                                        {tariff.signature && (
                                            <div className="tariff-title__subtitle text-xs text-opacity">
                                                <Typografed>{tariff.signature}</Typografed>
                                            </div>
                                        )}
                                    </div>
                                    <div className="tariff-info__container">
                                        {tariff.privilegeTitle && (
                                            <div className="tariff-info__title text-m">
                                                <Typografed>{tariff.privilegeTitle}</Typografed>{' '}
                                            </div>
                                        )}
                                        {tariff.features.length > 0 && (
                                            <ul className="tariff-info__description-list list-unstyled">
                                                {tariff.features.map((feature) => (
                                                    <li
                                                        className="tariff-info__description-item text-xs text-opacity"
                                                        key={feature.id}
                                                    >
                                                        <Typografed>{feature.name}</Typografed>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    <div className="tariff-card__bottom">
                                        {tariff.fullPrice && (
                                            <div className="tariff-bottom__container">
                                                <div className="tariff-price__full text-l text-opacity">
                                                    <Typografed>{tariff.fullPrice}</Typografed>
                                                </div>

                                                {showDescriptionOfPrice && (
                                                    <p className="tariff-card__price-desc text-xxs text-opacity">
                                                        {tp(DICT.TARIFF_DESCRIPTION[locale])}
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        <div className="tariff-bottom__container tariff-bottom__container--items-end">
                                            <div className="tariff-price__discount  text-xl">
                                                <Typografed>{tariff.discountPrice || tariff.fullPrice}</Typografed>
                                            </div>

                                            <div className="tariff-card__price-desc tariff-price__descr">
                                                {tariff.discountPrice && tariff.fullPrice && (
                                                    <p className="text-xs text-opacity">
                                                        <Typografed>При оплате до 10.09.2025</Typografed>
                                                    </p>
                                                )}
                                                {tariff.discountPrice && !tariff.fullPrice && (
                                                    <p className="text-xxs text-opacity">
                                                        {VAT ? (
                                                            <>{tp(DICT.TARIFF_DESCRIPTION[locale])}</>
                                                        ) : (
                                                            <>{tp(DICT.TARIFF_DESCRIPTION_WITHOUT_VAT[locale])}</>
                                                        )}
                                                    </p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="tariff-button__container">
                                            <Link
                                                target={tariff.button.isExternal ? '_blank' : undefined}
                                                rel={tariff.button.isExternal ? 'noreferrer nofollow' : undefined}
                                                href={tariff.button.href}
                                                className="header-btn blur-bg text-xs primary-header-btn"
                                                onClick={(e) => {
                                                    if (tariff.button.href === '/registration/') {
                                                        window._txq?.push(['track', 'prinyat_uchastie']);
                                                    }
                                                    window._txq?.push(['track', 'tarif_format']);
                                                }}
                                            >
                                                <Typografed>{tariff.button?.text || 'Принять участие'}</Typografed>
                                            </Link>
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        {contacts && tariffsInfo.length <= 3 && (
                            <div className="tariffs-info-section__contacts">
                                {(contacts.title || contacts.text) && (
                                    <div className="tariffs-info-section__contacts-header">
                                        {contacts.title && (
                                            <div className="text-l tariffs-info-section__contacts-title">
                                                <Typografed>{contacts.title}</Typografed>
                                            </div>
                                        )}
                                        {contacts.text && (
                                            <div
                                                className="text-xs tariffs-info-section__contacts-text"
                                                dangerouslySetInnerHTML={{
                                                    __html: tp(contacts.text),
                                                }}
                                            />
                                        )}
                                    </div>
                                )}
                                <ul className="list-unstyled tariffs-info-section__contact-list">
                                    {contacts.items.map((item) => (
                                        <li key={item.href}>
                                            <Link href={item.href} className="tariffs-info-section__contact-item">
                                                {item.icon && (
                                                    <RoundButton
                                                        tag="span"
                                                        className="tariffs-info-section__contact-item__icon"
                                                    >
                                                        <span
                                                            className="tariffs-info-section__contact-item__icon-img"
                                                            style={
                                                                {
                                                                    '--icon-url': `url(${item.icon.src})`,
                                                                } as React.CSSProperties
                                                            }
                                                            aria-hidden="true"
                                                        />
                                                    </RoundButton>
                                                )}
                                                <span className="text-xs tariffs-info-section__contact-item__label">
                                                    {item.label}
                                                </span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {oferta && oferta.src && (
                        <div className="tariffs-info-section__link-oferta">
                            <Link
                                href={oferta.src}
                                className="text-s link link--underlined"
                                target="_blank"
                                rel="noreferrer nofollow"
                            >
                                <Typografed>{oferta.title}</Typografed>
                            </Link>
                        </div>
                    )}
                </div>

                {notes && notes.length > 0 && (
                    <div className="tariffs-info-section__bottom">
                        {notes.map((note) => (
                            <p key={note} className="tariffs-info-section__note text-s text-opacity">
                                <Typografed>{note}</Typografed>
                            </p>
                        ))}
                    </div>
                )}
            </section>
        );
    },
);

TariffsInfoSection.displayName = 'TariffsInfoSection';

export default TariffsInfoSection;
